import React, { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { ProductSummary, TenantContext } from '../models/types';
import { ContextService } from '../services/contextService';
import { EntitlementService } from '../services/entitlementService';

interface PortalState {
  tenant?: TenantContext;
  products: ProductSummary[];
  entitlements: string[];
  loading: boolean;
  error?: string;
  refresh: () => Promise<void>;
}

const PortalContext = createContext<PortalState | undefined>(undefined);

export const PortalProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [tenant, setTenant] = useState<TenantContext>();
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [entitlements, setEntitlements] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      const context = await ContextService.loadTenant();
      let entitlements: string[] = context.entitlements ?? [];
      let productsList = context.productsOwned ?? [];

      if (entitlements.length === 0 || productsList.length === 0) {
        const entitlementResponse = await EntitlementService.load();
        productsList = productsList.length > 0 ? productsList : entitlementResponse.products;
        entitlements =
          entitlements.length > 0
            ? entitlements
            : entitlementResponse.entitlements ?? productsList.map((p) => p.id);
      }

      setTenant(context);
      setProducts(productsList);
      setEntitlements(entitlements);
    } catch (err) {
      if (err instanceof Error && err.message === 'AUTH_REDIRECT') {
        return;
      }
      setTenant(undefined);
      setProducts([]);
      setEntitlements([]);
      setError('We could not load your tenant data right now.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <PortalContext.Provider
      value={{
        tenant,
        products,
        entitlements,
        loading,
        error,
        refresh: load
      }}
    >
      {children}
    </PortalContext.Provider>
  );
};

export const usePortalContext = (): PortalState => {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('PortalContext is not available');
  }
  return context;
};
